import { create } from 'zustand';
import { acombaBridgeService, SessionExpiredError, InsufficientRightsError, BridgeNotOurLaunchError } from '../services/acombaBridgeService';
import { clearBridgeNonce } from '../services/bridgeNonce';
import { syncService } from '../services/syncService';
import { TranslationKey } from '../i18n/translationKeys';
import { getBridgeUrl } from '../utils/bridge/bridgeConfig';
import { bridgeSessionReasonKey } from '../utils/bridge/bridgeSessionError';
import { useTenantStore } from './tenantStore';
import { computeCompanyMatchStatus, type CompanyMatchStatus } from '../utils/bridge/companyMatch';
import { resolvePaymentLinks } from '../utils/relance/resolvePaymentLinks';
import type {
    BridgeHealthResult,
    ConnectionHealthResult,
    ConnectionHealthReason,
    ExtractionStatus,
    EntityExtractionStatus,
    SyncPayload,
    FunctionalityAccess,
    AcombaInvoiceAr,
} from '../types/acombaBridge';

/** The three Acomba interfaces extracted for a sync. */
export type ExtractionEntity = 'customers' | 'invoices' | 'payments';

// The Acomba bridge has a single COM session, so concurrent requests must not overlap. This holds
// the in-flight health check so callers (e.g. a sync about to extract) await the running one instead
// of racing it — a health check and an extraction hitting the bridge at once can deadlock it.
let inFlightHealthCheck: Promise<void> | null = null;

// Server-side cap for a single capped (cursor-less) invoice modified-since page. Module-level so the
// paging helper and the per-entity limits below can't drift apart.
const INVOICE_PAGE_LIMIT = 5000;

type SetState = (partial: Partial<AcombaBridgeState> | ((s: AcombaBridgeState) => Partial<AcombaBridgeState>)) => void;

/**
 * Internal helper — not part of the public store interface.
 * Fetches company info from the bridge and updates companyName / companyIcIdentifiantPublique /
 * companyMatchStatus / companyTransphereUniqueId / companyTransphereEncaissementActif. Fail-open: on
 * error, prior values are retained (companyMatchStatus stays at its last-known value, not reset) so
 * the connected state is never broken by a failing /api/companies call. Callers must only invoke this
 * after the session transitions to 'connected'.
 */
async function fetchAndSetCompanyInfo(set: SetState): Promise<void> {
    const bridgeUrl = getBridgeUrl();
    try {
        const info = await acombaBridgeService.getCompany(bridgeUrl);
        const bridgeKey = info.icIdentifiantPublique ?? null;
        const launchedKey = useTenantStore.getState().launchedIcIdentifiantPublique;
        const matchStatus = computeCompanyMatchStatus(launchedKey, bridgeKey);
        set({
            companyName: info.name ?? null,
            companyIcIdentifiantPublique: bridgeKey,
            companyMatchStatus: matchStatus,
            companyInfoStatus: 'resolved',
            companyTransphereUniqueId: info.transphereUniqueId ?? null,
            companyTransphereEncaissementActif: info.transphereEncaissementActif ?? false,
            // `?? null` keeps "unknown" distinct from "off": an older bridge omits the field, and only an
            // explicit false licenses treating this company as having no payment links at all.
            companyTransphereActive: info.transphereActive ?? null,
        });

        // Report the société's identity to the backend now, for the same reason the access flag is
        // reported here: a reminder sent from this session goes out as the business, and the backend
        // cannot reach the bridge to ask.
        //
        // Gated on the same predicate as the sync itself (see bridgeSyncBlockReason): on a mismatch the
        // bridge is pointed at a different company, so pushing would stamp that company's name and email
        // onto the launched tenant's reminders. 'unverifiable' is allowed through — we cannot confirm the
        // company, but blocking would leave a legitimately manual setup with no identity at all.
        //
        // Best-effort — a failure only costs the per-company display name and reply-to on this session's
        // sends, so it must not break the connected state.
        if (matchStatus !== 'mismatch') {
            try {
                await syncService.setCompanyIdentity(info.name ?? null, info.emailAddress ?? null);
            } catch (e) {
                console.debug('Failed to report the company identity to the backend:', e);
            }
        }
    } catch {
        // Fail-open: companyName/companyIcIdentifiantPublique/companyMatchStatus/
        // companyTransphereUniqueId/companyTransphereEncaissementActif retain their prior values. The
        // connected state is not affected; sync may still proceed unless companyMatchStatus was already
        // 'mismatch' from a previous successful fetch. companyInfoStatus does flip to 'failed' though,
        // so a consumer reading the Transphere fields as fact (useTransphereStatus) can tell a stale
        // "not subscribed" default apart from a genuinely resolved one.
        set({ companyInfoStatus: 'failed' });
    }
}

/**
 * Internal helper — the bridge process itself did not answer (`/health` threw or came back empty), so
 * nothing about a prior session can still be trusted. Without this, a session that was 'connected'
 * before the bridge went unreachable kept reading as 'connected' forever after (checkSession, and
 * therefore this whole reset, is never reached once /health fails) — HomeGreeting kept showing the
 * previous userName, and useAcombaBridgeGate never noticed the disconnect on its background poll.
 * Mirrors checkSession's own 'not connected' reset, minus the session-specific reason/error fields,
 * which only make sense for a session the bridge actually answered about.
 *
 * Also clears bridgeInfo: leaving the last-known health record in place would let
 * CopyDiagnosticsButton print a stale "healthy" block (bridge version, SDK version, etc.) right next
 * to cause: unreachable — a self-contradicting diagnostics dump the moment support needs it most.
 */
function markBridgeUnreachable(set: SetState): void {
    receiptSubscriptionAnsweredFor = undefined;
    set({
        bridgeStatus: 'unreachable',
        bridgeInfo: null,
        sessionStatus: 'disconnected',
        sessionInfo: null,
        sessionReason: null,
        sessionErrorMessage: null,
        companyName: null,
        companyIcIdentifiantPublique: null,
        companyMatchStatus: 'unknown',
        companyInfoStatus: 'unknown',
        companyTransphereUniqueId: null,
        companyTransphereEncaissementActif: false,
        companyTransphereReceiptSubscription: null,
        companyTransphereActive: null,
        functionalityAccess: null,
    });
}

/**
 * Internal helper — fetches the connected user's per-functionality access from the bridge and stores
 * it. Fail-open: on error, functionalityAccess is left as-is (null = unknown → not blocked); a real
 * denial is still caught reactively during extraction (InsufficientRightsError). Call only after the
 * session is 'connected'.
 */
async function fetchAndSetAccess(set: SetState, companyMatchStatus: CompanyMatchStatus): Promise<void> {
    const bridgeUrl = getBridgeUrl();
    try {
        const access = await acombaBridgeService.getAccess(bridgeUrl);
        set({ functionalityAccess: access });

        // Report it to the backend right now (session start), not just as part of a later sync
        // payload, so the persisted decision (Home/History gating) is fresh from the moment the app
        // opens rather than waiting for whichever sync happens to run next. Best-effort: this call
        // failing must not block using the app — the sync-time refresh remains the backstop.
        if (companyMatchStatus !== 'mismatch') {
            try {
                await syncService.setRelanceAccess(access.relance, access.isSupervisor);
            } catch (e) {
                // Ignored — see comment above. Logged only for diagnosability (e.g. a 401 means the
                // decision was silently never persisted this session).
                console.debug('Failed to report relance access to the backend:', e);
            }
        }
    } catch {
        // Fail-open: keep prior value; the reactive rights error during extraction remains the backstop.
    }
}

async function fetchAllPages<T>(
    fetcher: (cardPos: number) => Promise<{ data: T[]; hasMore: boolean; nextCursor: number | null }>,
    getCancelled: () => boolean,
): Promise<T[]> {
    const all: T[] = [];
    let cardPos = 1;
    while (true) {
        if (getCancelled()) break;
        const page = await fetcher(cardPos);
        all.push(...page.data);
        if (!page.hasMore || page.nextCursor === null) break;
        cardPos = page.nextCursor;
    }
    return all;
}

// The invoice-date lower bound for a full extraction: the backend's *effective* sync start date (the
// configured date, or the 6-month rolling default). Bounding the source read here keeps the full read
// from walking the whole AR history — it mirrors the backend's start-date invoice filter. Acomba-only.
// Fails open to undefined (unbounded read) so a start-date endpoint hiccup can never block a sync.
async function resolveInvoiceExtractionSince(): Promise<string | undefined> {
    try {
        return (await syncService.getSyncStartDate()).effectiveStartDate ?? undefined;
    } catch {
        return undefined;
    }
}

// How far back to move the modification cutoff before handing it to the bridge.
//
// `since` is the backend's lastSyncedAt, stamped with server UTC *after* the sync finishes, but it is
// compared against InTimeModified — the Acomba machine's own wall clock. Two ways that loses records
// outright: anything modified while the previous sync was mid-walk sits before the timestamp the sync
// eventually recorded, and any clock skew between the Acomba PC and the server shifts every cutoff into
// the Acomba future, permanently skipping the tail of each interval.
//
// The old date-index read compared on `.Date`, which gave it up to a day of slack for free; an exact
// instant has none. A day of overlap restores it. Re-reading a day of changed records costs nothing here:
// the walk visits every card position regardless and computes no balances, and the per-customer re-read
// downstream is keyed on the customer set, not the record count.
const CUTOFF_MARGIN_MS = 24 * 60 * 60 * 1000;

// The société an answer was obtained for; `undefined` while none has been. Keyed rather than a flag so a
// société switched inside Acomba cannot inherit the previous one's answer.
let receiptSubscriptionAnsweredFor: string | null | undefined;
let readingReceiptSubscription = false;

// The link resolution reports the société's receipt subscription along the way; only a read answer is
// recorded, so a resolution that never reached Transphere leaves the previous value for the backend.
//
// Reaching Transphere at all counts as the answer for this société, exactly as the connect-time ask
// counts it: the callback only runs on a resolved batch, so stamping here stops the health poll from
// launching a second resolver process for what the sync just obtained.
function reportReceiptSubscription(
    set: (partial: Partial<AcombaBridgeState>) => void,
    companyKey: string | null,
) {
    return (active: boolean | null) => {
        receiptSubscriptionAnsweredFor = companyKey;
        if (active !== null) set({ companyTransphereReceiptSubscription: active });
    };
}

/**
 * Asks Transphere whether the société's receipt subscription is active, on an empty link-resolution
 * batch — the one request that reads it without an unpaid invoice carrying a Transphere id. Resolution
 * during a sync reports it too; this only moves the first answer earlier, so an already-synced tenant
 * does not spend the whole visit treating a lapsed subscription as active.
 *
 * Best-effort: an older bridge, an unreachable Transphere or a refused credential all leave the value
 * unknown, which fails open, rather than recording a "no".
 */
async function fetchAndSetReceiptSubscription(set: SetState, companyKey: string | null): Promise<void> {
    readingReceiptSubscription = true;
    try {
        const result = await acombaBridgeService.resolveTransphereLinks(getBridgeUrl(), []);
        // Transphere was never reached, so nothing was learned and the next connect may ask again.
        if (!result.resolved) return;

        receiptSubscriptionAnsweredFor = companyKey;
        if (result.receiptSubscriptionActive != null) {
            set({ companyTransphereReceiptSubscription: result.receiptSubscriptionActive });
        }
    } catch {
        // Every failure here is "unknown", never "no subscription".
    } finally {
        readingReceiptSubscription = false;
    }
}

function shouldReadReceiptSubscription(state: AcombaBridgeState): boolean {
    if (state.companyInfoStatus !== 'resolved') return false;
    // Same refusal as the company-identity push: on a mismatch the answer belongs to another société.
    if (state.companyMatchStatus === 'mismatch') return false;
    if (state.companyTransphereActive === false) return false;
    // An extraction owns the bridge, and asks on its own way past.
    if (state.isExtracting) return false;
    if (readingReceiptSubscription) return false;

    // Asked once per société: checkSession runs on a health poll every minute, and each ask launches a
    // resolver process.
    return receiptSubscriptionAnsweredFor !== state.companyIcIdentifiantPublique;
}

function withCutoffMargin(since: string): string {
    const parsed = Date.parse(since);
    // Unparseable cutoff → hand it through untouched rather than invent one; the bridge rejects a bad value.
    return Number.isNaN(parsed) ? since : new Date(parsed - CUTOFF_MARGIN_MS).toISOString();
}

// Discovers the AR records that changed since the cutoff, preferring the bridge's changed-since walk
// (InTimeModified) over the older dated-since read (InDate).
//
// The distinction is the whole point: InvoiceAr has no index led by a modified time, so a dated-since read
// answers "dated since", not "changed since". It therefore cannot see a transaction entered today under an
// earlier invoice date — a late data entry — nor an edit to an old invoice, and misses both silently.
//
// Falls back to the dated-since read when the bridge predates the endpoint (null). The bridge ships
// bundled with Acomba per site, so an older site keeps its previous behaviour instead of being refused,
// which is what lets this land without raising MIN_BRIDGE_VERSION.
//
// Neither read catches a payment applied to an old invoice: that changes the balance without touching the
// AR record's modified time. The payment modified-since read remains the only cover for those.
async function discoverChangedInvoices(
    bridgeUrl: string,
    since: string,
    getCancelled: () => boolean,
): Promise<{ items: AcombaInvoiceAr[]; truncated: boolean; usedChangedSince: boolean }> {
    // Bound the walk's returned records to the sync window, exactly as the full extraction does.
    const startDate = await resolveInvoiceExtractionSince();
    const changeSet = await acombaBridgeService.getInvoiceArsChangedSince(bridgeUrl, withCutoffMargin(since), startDate);

    if (changeSet) {
        // One walk, no cap and no cursor — so nothing can be truncated the way a capped page can.
        return { items: changeSet.changed, truncated: false, usedChangedSince: true };
    }

    const dated = await fetchAllModifiedSince(
        before => acombaBridgeService.getInvoiceArsModifiedSince(bridgeUrl, since, INVOICE_PAGE_LIMIT, before),
        INVOICE_PAGE_LIMIT,
        i => i.cardPos,
        i => i.transactionDate,
        getCancelled,
        'invoices',
    );
    return { items: dated.items, truncated: dated.truncated, usedChangedSince: false };
}

// Pages a bridge "modified-since" read that is ordered newest-first and caps each response at `limit`.
// Resumes via an inclusive `before` upper-bound cursor (the oldest record of the previous page), so the
// bridge re-emits the boundary record — hence the dedup by unique key. Returns the deduped set and
// whether it stopped without draining the window (a single-cursor block larger than `limit` can't be
// paged without a finer key — pathological; surfaced so the caller can flag the sync partial).
async function fetchAllModifiedSince<T>(
    fetchPage: (before: string | undefined) => Promise<{ data: T[] }>,
    limit: number,
    keyOf: (item: T) => number,
    cursorOf: (item: T) => string,
    getCancelled: () => boolean,
    label: string,
): Promise<{ items: T[]; truncated: boolean }> {
    const items: T[] = [];
    const seen = new Set<number>();
    let before: string | undefined;
    let truncated = false;

    while (!getCancelled()) {
        const page = await fetchPage(before);
        for (const item of page.data) {
            const key = keyOf(item);
            if (!seen.has(key)) { seen.add(key); items.push(item); }
        }
        if (page.data.length < limit) break; // drained the window down to `since`
        const oldest = cursorOf(page.data[page.data.length - 1]);
        if (before !== undefined && oldest === before) {
            // The whole page shares one cursor value — can't advance without a finer key.
            truncated = true;
            console.warn(`[acombaBridge] incremental ${label} read cannot page past a single-cursor block larger than ${limit}; results may be truncated.`);
            break;
        }
        before = oldest;
    }

    return { items, truncated };
}

// Pages a read by carrying a cursor derived from the last item of each page. The cursor must be the page
// order's OWN key: the by-customer invoice index orders by (invoiceNumber, unique), so resuming on card
// position — a physical slot, non-monotonic in that order — would drop rows whose slot sits below the page
// boundary. The cursor is exclusive, so no dedup is needed.
async function fetchAllByCursor<T, C>(
    fetchPage: (cursor: C | undefined) => Promise<{ data: T[] }>,
    getCursor: (last: T) => C,
    limit: number,
    getCancelled: () => boolean,
): Promise<T[]> {
    const all: T[] = [];
    let cursor: C | undefined;
    while (!getCancelled()) {
        const page = await fetchPage(cursor);
        all.push(...page.data);
        if (page.data.length < limit) break;
        cursor = getCursor(page.data[page.data.length - 1]);
    }
    return all;
}

const defaultExtractionStatus: ExtractionStatus = {
    customers: 'idle',
    invoices: 'idle',
    payments: 'idle',
    isPartial: false,
};

interface AcombaBridgeState {
    bridgeStatus: 'checking' | 'reachable' | 'unreachable';
    sessionStatus: 'idle' | 'connected' | 'disconnected' | 'error';
    isCheckingHealth: boolean;
    bridgeInfo: BridgeHealthResult | null;
    sessionInfo: ConnectionHealthResult | null;
    sessionErrorMessage: string | null;
    // Machine-readable cause when the session isn't connected (from the bridge health contract). Drives
    // the distinct SDK-unavailable treatment in the icon/page. Null when connected or cause is unknown.
    sessionReason: ConnectionHealthReason | null;
    isExtracting: boolean;
    extractionCancelled: boolean;
    extractionStatus: ExtractionStatus;
    syncPayload: SyncPayload | null;
    // Company info fetched from the bridge on connect
    companyName: string | null;
    companyIcIdentifiantPublique: string | null;
    /** CITransphereActive for the connected société. Null = unknown (older bridge), not false. */
    companyTransphereActive: boolean | null;
    // Match status between the launched société key and the bridge's connected company key.
    // Only meaningful when sessionKind === 'desktop' and sessionStatus === 'connected'.
    companyMatchStatus: CompanyMatchStatus;
    // Outcome of the last /api/companies fetch. 'unknown' before the first attempt, 'failed' when the
    // call errored (fetchAndSetCompanyInfo is fail-open, so the Transphere fields below then still
    // hold their prior — possibly default — values). Consumers must check this before reading the
    // Transphere fields as fact: sessionStatus === 'connected' alone does not mean the fetch resolved.
    companyInfoStatus: 'unknown' | 'resolved' | 'failed';
    // ACCEO Transphere state for the connected société, echoed by the bridge from Acomba's push (see
    // CompanyInformation). Null/false until sessionStatus === 'connected' and the fetch resolves —
    // consumers (useTransphereStatus) must gate on companyInfoStatus, not treat these as "not subscribed".
    companyTransphereUniqueId: string | null;
    companyTransphereEncaissementActif: boolean;
    /**
     * Whether Transphere will take a customer payment for this société, as the last link resolution
     * reported it. Null until one has run, and cleared on disconnect like the other société facts.
     */
    companyTransphereReceiptSubscription: boolean | null;
    // Per-functionality access for the connected Acomba user. Null until fetched / on error (unknown →
    // not blocked). Acomba-only — the AcceoConnect/AcombaGo path never reads this.
    functionalityAccess: FunctionalityAccess | null;

    checkBridgeHealth: () => Promise<void>;
    checkSession: () => Promise<void>;
    /**
     * Extracts customers, invoices and payments. Returns the interfaces that did NOT complete (failed or
     * cancelled) — an empty array means a complete, safe-to-sync dataset. Callers must not push an
     * incomplete payload: a missing interface yields wrong balances/relances and, on a Full sync, can wipe
     * relances via cleanup. Session-expired / insufficient-rights null the payload and surface via
     * sessionStatus.
     *
     * Returns **null** when the extraction did not run at all (one already in flight, or the bridge is on
     * a different company than the one launched). That is a distinct outcome from "ran and nothing
     * failed", and callers must not post a payload on it: `syncPayload` would then hold whatever a
     * PREVIOUS extraction left behind. Returning `[]` here is what let a second click push stale data.
     */
    startExtraction: () => Promise<ExtractionEntity[] | null>;
    /**
     * Incremental extraction: fetches only records changed since `since` (ISO-8601 DateTime string).
     * Customers and payments use the bridge's modified-since index. Invoices are a union of:
     *   1. Invoices dated since `since` (new invoices, via date index).
     *   2. Invoices for any customer that has a payment modified since `since` (re-reads old invoices
     *      whose AR balance changed due to a new payment — InvoiceAR has no TimeModified index, so
     *      payment-based inference is the only reliable way to catch these balance updates).
     * When the union produces duplicates (same invoice in both sets), the dated-since copy is kept
     * (both are identical snapshots; deduplication is by CardPos). Returns the interfaces that did NOT
     * complete (failed or cancelled), same contract as startExtraction.
     */
    startIncrementalExtraction: (since: string) => Promise<ExtractionEntity[] | null>;
    cancelExtraction: () => void;
    retryFailedEntity: (entity: 'customers' | 'invoices' | 'payments') => Promise<void>;
}

export const useAcombaBridgeStore = create<AcombaBridgeState>((set, get) => ({
    bridgeStatus: 'checking',
    sessionStatus: 'idle',
    isCheckingHealth: false,
    bridgeInfo: null,
    sessionInfo: null,
    sessionErrorMessage: null,
    sessionReason: null,
    isExtracting: false,
    extractionCancelled: false,
    extractionStatus: defaultExtractionStatus,
    syncPayload: null,
    companyName: null,
    companyIcIdentifiantPublique: null,
    companyTransphereActive: null,
    companyMatchStatus: 'unknown',
    companyInfoStatus: 'unknown',
    companyTransphereUniqueId: null,
    companyTransphereEncaissementActif: false,
    companyTransphereReceiptSubscription: null,
    functionalityAccess: null,

    async checkBridgeHealth() {
        // Never health-check while an extraction is using the bridge's single COM session.
        if (get().isExtracting) return;
        // Coalesce concurrent callers onto one check: a caller awaiting this (e.g. a sync before it
        // extracts) must wait for the running check to finish rather than proceed and race it.
        if (inFlightHealthCheck) return inFlightHealthCheck;

        inFlightHealthCheck = (async () => {
            set({ isCheckingHealth: true });
            try {
                const bridgeUrl = getBridgeUrl();
                const result = await acombaBridgeService.checkHealth(bridgeUrl);
                if (typeof result?.status !== 'string' || !result.status) {
                    markBridgeUnreachable(set);
                    return;
                }
                set({ bridgeStatus: 'reachable', bridgeInfo: result });
                await get().checkSession();
            } catch {
                markBridgeUnreachable(set);
            } finally {
                set({ isCheckingHealth: false });
                inFlightHealthCheck = null;
            }
        })();

        return inFlightHealthCheck;
    },

    async checkSession() {
        const { bridgeStatus } = get();
        if (bridgeStatus !== 'reachable') return;
        const bridgeUrl = getBridgeUrl();
        try {
            const result = await acombaBridgeService.checkSession(bridgeUrl);
            if (result.connected) {
                // Resolve company info + access BEFORE flipping sessionStatus to 'connected'. A Zustand
                // set notifies subscribers synchronously, so any reactive consumer that gates on access
                // must not observe 'connected' while companyIcIdentifiantPublique / functionalityAccess
                // are still null — that window would let a gated action run against unresolved access.
                set({ sessionInfo: result, sessionErrorMessage: null, sessionReason: null });
                await fetchAndSetCompanyInfo(set);
                await fetchAndSetAccess(set, get().companyMatchStatus);
                set({ sessionStatus: 'connected' });

                // Not awaited, and after the flip to 'connected': a Transphere round trip must not hold
                // up the session every consumer waits on, and an unknown subscription fails open.
                if (shouldReadReceiptSubscription(get())) {
                    void fetchAndSetReceiptSubscription(set, get().companyIcIdentifiantPublique);
                }
            } else {
                receiptSubscriptionAnsweredFor = undefined;
                set({ sessionStatus: 'disconnected', sessionInfo: null, sessionReason: result.reason ?? null, sessionErrorMessage: bridgeSessionReasonKey(result.reason ?? null), companyName: null, companyIcIdentifiantPublique: null, companyMatchStatus: 'unknown', companyInfoStatus: 'unknown', companyTransphereUniqueId: null, companyTransphereEncaissementActif: false, companyTransphereReceiptSubscription: null, companyTransphereActive: null, functionalityAccess: null });
            }
        } catch (err) {
            if (err instanceof BridgeNotOurLaunchError) {
                // The nonce cannot become valid again, and the poller would resend it every minute.
                clearBridgeNonce();
                set({ sessionStatus: 'error', sessionErrorMessage: TranslationKey.AcombaBridge.SessionReason.NotOurLaunch, sessionReason: null });
                return;
            }
            // The session health call threw (bridge reachable but errored/timed out) — surface the
            // generic localized "couldn't connect" key, not the raw English error. Keep the underlying
            // error in the console for debugging.
            console.warn('[acombaBridge] session check failed', err);
            set({ sessionStatus: 'error', sessionErrorMessage: TranslationKey.AcombaBridge.SessionReason.Unknown, sessionReason: null });
        }
    },

    async startExtraction(): Promise<ExtractionEntity[] | null> {
        if (get().isExtracting) return null;
        // Wait out any in-flight health check so extraction never overlaps it on the bridge's single
        // COM session (concurrent bridge calls can deadlock it — the cause of a stuck sync).
        if (inFlightHealthCheck) await inFlightHealthCheck;
        // Guard: block extraction when the bridge is connected to a different company than the
        // one the user launched from. 'unverifiable' (manual setup, no key to compare) is allowed
        // through — we can't confirm but also can't block in that case.
        if (get().companyMatchStatus === 'mismatch') return null;

        const bridgeUrl = getBridgeUrl();
        set({
            // Drop the previous payload before reading a new one. It exists only to be posted by the run
            // that produced it, so keeping it alive across runs is what made stale data postable at all.
            // A sync always sends what THIS extraction read, or nothing.
            syncPayload: null,
            isExtracting: true,
            extractionCancelled: false,
            extractionStatus: { customers: 'idle', invoices: 'idle', payments: 'idle', isPartial: false },
        });

        let customers: SyncPayload['customers'] = [];
        let invoices: SyncPayload['invoices'] = [];
        let payments: SyncPayload['payments'] = [];
        const statuses: Record<'customers' | 'invoices' | 'payments', EntityExtractionStatus> = {
            customers: 'idle',
            invoices: 'idle',
            payments: 'idle',
        };

        const getCancelled = () => get().extractionCancelled;

        const updateStatus = (entity: 'customers' | 'invoices' | 'payments', status: EntityExtractionStatus) => {
            statuses[entity] = status;
            set((s) => ({ extractionStatus: { ...s.extractionStatus, [entity]: status } }));
        };

        const handleSessionExpired = () => {
            set({
                isExtracting: false,
                sessionStatus: 'disconnected',
                sessionErrorMessage: TranslationKey.AcombaBridge.Credentials.SessionExpiredMsg,
                syncPayload: null,
            });
        };

        const handleInsufficientRights = () => {
            set({
                isExtracting: false,
                sessionStatus: 'disconnected',
                sessionErrorMessage: TranslationKey.AcombaBridge.Errors.InsufficientRights,
                syncPayload: null,
            });
        };

        // Customers
        updateStatus('customers', 'fetching');
        try {
            if (!getCancelled()) {
                customers = await fetchAllPages(
                    (cardPos) => acombaBridgeService.getCustomers(bridgeUrl, cardPos),
                    getCancelled,
                );
                updateStatus('customers', getCancelled() ? 'cancelled' : 'done');
            } else {
                updateStatus('customers', 'cancelled');
            }
        } catch (err) {
            if (err instanceof SessionExpiredError) { handleSessionExpired(); return []; }
            if (err instanceof InsufficientRightsError) { handleInsufficientRights(); return []; }
            updateStatus('customers', 'failed');
        }

        // Invoices — bounded at the source by the sync start date (Acomba-only): the bridge walks all
        // card positions but only prices/returns invoices dated on/after it (plus in-transit records),
        // so a full sync no longer pulls the whole AR history. Falls open to an unbounded read when the
        // start date can't be resolved.
        if (!getCancelled()) {
            updateStatus('invoices', 'fetching');
            try {
                const invoiceSince = await resolveInvoiceExtractionSince();
                invoices = await fetchAllPages(
                    (cardPos) => acombaBridgeService.getInvoiceArs(bridgeUrl, cardPos, 200, invoiceSince),
                    getCancelled,
                );
                updateStatus('invoices', getCancelled() ? 'cancelled' : 'done');
            } catch (err) {
                if (err instanceof SessionExpiredError) { handleSessionExpired(); return []; }
                if (err instanceof InsufficientRightsError) { handleInsufficientRights(); return []; }
                updateStatus('invoices', 'failed');
            }
        } else {
            updateStatus('invoices', 'cancelled');
        }

        // Payments
        if (!getCancelled()) {
            updateStatus('payments', 'fetching');
            try {
                payments = await fetchAllPages(
                    (cardPos) => acombaBridgeService.getCustomerPayments(bridgeUrl, cardPos),
                    getCancelled,
                );
                updateStatus('payments', getCancelled() ? 'cancelled' : 'done');
            } catch (err) {
                if (err instanceof SessionExpiredError) { handleSessionExpired(); return []; }
                if (err instanceof InsufficientRightsError) { handleInsufficientRights(); return []; }
                updateStatus('payments', 'failed');
            }
        } else {
            updateStatus('payments', 'cancelled');
        }

        const failed = (Object.keys(statuses) as ExtractionEntity[]).filter(
            (e) => statuses[e] === 'failed' || statuses[e] === 'cancelled',
        );

        const paymentLinks = await resolvePaymentLinks(bridgeUrl, invoices, getCancelled, get().companyTransphereActive, reportReceiptSubscription(set, get().companyIcIdentifiantPublique));

        set((state) => ({
            isExtracting: false,
            syncPayload: { customers, invoices, payments, paymentLinks },
            extractionStatus: { ...state.extractionStatus, isPartial: failed.length > 0 },
        }));

        // Non-empty ⇒ incomplete: callers must abort rather than push a partial (wrong-relance) payload.
        return failed;
    },

    async startIncrementalExtraction(since: string): Promise<ExtractionEntity[] | null> {
        if (get().isExtracting) return null;
        if (inFlightHealthCheck) await inFlightHealthCheck;
        if (get().companyMatchStatus === 'mismatch') return null;

        const bridgeUrl = getBridgeUrl();
        set({
            syncPayload: null,
            isExtracting: true,
            extractionCancelled: false,
            extractionStatus: { customers: 'idle', invoices: 'idle', payments: 'idle', isPartial: false },
        });

        let customers: SyncPayload['customers'] = [];
        const invoices: SyncPayload['invoices'] = [];
        let payments: SyncPayload['payments'] = [];
        const statuses: Record<'customers' | 'invoices' | 'payments', EntityExtractionStatus> = {
            customers: 'idle',
            invoices: 'idle',
            payments: 'idle',
        };

        // Modified-since reads are single, server-capped bridge calls with no cursor
        // (PagedResult.FromFiltered carries no HasMore). A returned page at the cap means more records
        // changed than the cap allows and the tail was dropped — flag the sync partial so the backend
        // treats it as incomplete instead of silently losing data. Proper pagination would need cursor
        // support on the bridge modified-since endpoints (tracked as a follow-up).
        const pageLimit = { customers: 2000, payments: 5000, invoices: INVOICE_PAGE_LIMIT, customerInvoices: 2000 } as const;
        let truncated = false;
        const flagIfTruncated = (received: number, limit: number, source: string) => {
            if (received >= limit) {
                truncated = true;
                console.warn(`[acombaBridge] incremental ${source} read hit the ${limit}-record cap; results may be truncated.`);
            }
        };

        const getCancelled = () => get().extractionCancelled;

        const updateStatus = (entity: 'customers' | 'invoices' | 'payments', status: EntityExtractionStatus) => {
            statuses[entity] = status;
            set((s) => ({ extractionStatus: { ...s.extractionStatus, [entity]: status } }));
        };

        const handleSessionExpired = () => {
            set({
                isExtracting: false,
                sessionStatus: 'disconnected',
                sessionErrorMessage: TranslationKey.AcombaBridge.Credentials.SessionExpiredMsg,
                syncPayload: null,
            });
        };

        const handleInsufficientRights = () => {
            set({
                isExtracting: false,
                sessionStatus: 'disconnected',
                sessionErrorMessage: TranslationKey.AcombaBridge.Errors.InsufficientRights,
                syncPayload: null,
            });
        };

        // Customers modified since cutoff
        updateStatus('customers', 'fetching');
        try {
            if (!getCancelled()) {
                const result = await fetchAllModifiedSince(
                    before => acombaBridgeService.getCustomersModifiedSince(bridgeUrl, since, pageLimit.customers, before),
                    pageLimit.customers,
                    c => c.cardPos,
                    c => c.timeModified,
                    getCancelled,
                    'customers',
                );
                customers = result.items;
                if (result.truncated) truncated = true;
                updateStatus('customers', getCancelled() ? 'cancelled' : 'done');
            } else {
                updateStatus('customers', 'cancelled');
            }
        } catch (err) {
            if (err instanceof SessionExpiredError) { handleSessionExpired(); return []; }
            if (err instanceof InsufficientRightsError) { handleInsufficientRights(); return []; }
            updateStatus('customers', 'failed');
        }

        // Payments modified since cutoff
        if (!getCancelled()) {
            updateStatus('payments', 'fetching');
            try {
                const result = await acombaBridgeService.getCustomerPaymentsModifiedSince(bridgeUrl, since, pageLimit.payments);
                flagIfTruncated(result.data.length, pageLimit.payments, 'payments');
                payments = result.data;
                updateStatus('payments', getCancelled() ? 'cancelled' : 'done');
            } catch (err) {
                if (err instanceof SessionExpiredError) { handleSessionExpired(); return []; }
                if (err instanceof InsufficientRightsError) { handleInsufficientRights(); return []; }
                updateStatus('payments', 'failed');
            }
        } else {
            updateStatus('payments', 'cancelled');
        }

        // Every customer touched since the cutoff — by a modified record, a new invoice, or a new
        // payment — must be sent with their FULL invoice set (and full customer record). The backend
        // recomputes the relance total from the payload and REPLACES the stored value, so a partial
        // invoice set for an affected customer would clobber their real balance. This mirrors the
        // Connect partial path, which re-fetches complete data per affected customer.
        //
        // (InvoiceAR has no TimeModified index, so a payment against an old invoice changes its balance
        // but not its date — re-reading the whole customer is what catches those balance changes.)
        if (!getCancelled()) {
            updateStatus('invoices', 'fetching');
            try {
                // This read only DISCOVERS customers whose AR changed (including existing customers whose
                // own record wasn't re-modified); the invoices actually sent come from the per-customer
                // full read below.
                const discovered = await discoverChangedInvoices(bridgeUrl, since, getCancelled);
                if (discovered.truncated) truncated = true;

                const customerByNumber = new Map(customers.map(c => [c.clientId, c]));
                const affectedNumbers = new Set<string>([
                    ...customers.map(c => c.clientId),
                    ...discovered.items.map(i => i.clientId),
                    // Bill-to ("Facturer à") customer of each dated invoice: downstream the invoice is
                    // attributed to this master customer (BridgeTransactionMapper). If the master wasn't
                    // independently touched since the cutoff it must still be re-fetched here so its
                    // hashing-derived identity is in the payload — otherwise the invoice resolves to a
                    // different, number-derived id and the customer's identity splits.
                    ...discovered.items
                        .map(i => i.invoicedToClientId)
                        .filter((n): n is string => !!n),
                    ...payments.map(p => p.customerNumber),
                ]);

                const seenInvoiceCardPos = new Set<number>();
                for (const customerNumber of affectedNumbers) {
                    if (getCancelled()) break;

                    try {
                        let customer = customerByNumber.get(customerNumber) ?? null;
                        if (!customer) {
                            // Affected via an invoice/payment but the record itself wasn't modified —
                            // fetch it so the relance can recompute credit %/flags from the full record.
                            customer = await acombaBridgeService.getCustomerByNumber(bridgeUrl, customerNumber);
                            if (customer) {
                                customerByNumber.set(customerNumber, customer);
                                customers.push(customer);
                            }
                        }
                        if (!customer) continue;

                        const customerInvoices = await fetchAllByCursor(
                            (cursor: { invoiceNumber: string; unique: number } | undefined) => acombaBridgeService.getInvoiceArsByCustomerCardPos(
                                bridgeUrl,
                                customer.cardPos,
                                pageLimit.customerInvoices,
                                cursor?.invoiceNumber,
                                cursor?.unique,
                            ),
                            invoice => ({ invoiceNumber: invoice.invoiceNumber, unique: invoice.unique }),
                            pageLimit.customerInvoices,
                            getCancelled,
                        );
                        for (const invoice of customerInvoices) {
                            if (!seenInvoiceCardPos.has(invoice.cardPos)) {
                                seenInvoiceCardPos.add(invoice.cardPos);
                                invoices.push(invoice);
                            }
                        }
                    } catch (err) {
                        // Re-throw session/rights errors so the outer handler can terminate cleanly.
                        // For other errors (e.g. network timeout on one customer), skip this customer
                        // and continue — partial invoice data is better than losing all invoices.
                        if (err instanceof SessionExpiredError || err instanceof InsufficientRightsError) throw err;
                        // Non-fatal: mark result as partial so the backend knows this sync was incomplete.
                        statuses['invoices'] = 'failed';
                    }
                }

                // Preserve a per-customer read failure recorded inside the loop above — the post-loop
                // status must not overwrite 'failed' with 'done', or the partial signal (isPartial)
                // would be lost and the backend would treat an incomplete sync as complete.
                const invoiceStatus = getCancelled()
                    ? 'cancelled'
                    : statuses['invoices'] === 'failed' ? 'failed' : 'done';
                updateStatus('invoices', invoiceStatus);
            } catch (err) {
                if (err instanceof SessionExpiredError) { handleSessionExpired(); return []; }
                if (err instanceof InsufficientRightsError) { handleInsufficientRights(); return []; }
                updateStatus('invoices', 'failed');
            }
        } else {
            updateStatus('invoices', 'cancelled');
        }

        const failed = (Object.keys(statuses) as ExtractionEntity[]).filter(
            (e) => statuses[e] === 'failed' || statuses[e] === 'cancelled',
        );
        // Truncation drops the tail of an interface that otherwise read fine — it flags the payload
        // partial (extractionStatus.isPartial, surfaced in the UI and forcing a re-extraction on the
        // next auto-sync) but is not an interface failure, so it does not enter `failed`: callers using
        // `failed.length > 0` won't abort on truncation. `isPartial` is not part of the sync request.
        const isPartial = truncated || failed.length > 0;

        const paymentLinks = await resolvePaymentLinks(bridgeUrl, invoices, getCancelled, get().companyTransphereActive, reportReceiptSubscription(set, get().companyIcIdentifiantPublique));

        set((state) => ({
            isExtracting: false,
            syncPayload: { customers, invoices, payments, paymentLinks },
            extractionStatus: { ...state.extractionStatus, isPartial },
        }));

        return failed;
    },

    // Intentionally retained though no UI currently triggers it: the read-only status page has no
    // cancel control, but the extraction methods still honour the extractionCancelled flag so a future
    // page that re-exposes extraction can wire a cancel button back to this action without re-plumbing.
    cancelExtraction() {
        set({ extractionCancelled: true });
    },

    async retryFailedEntity(entity) {
        const bridgeUrl = getBridgeUrl();
        const { syncPayload, extractionStatus, companyMatchStatus } = get();
        if (extractionStatus[entity] !== 'failed') return;
        if (companyMatchStatus === 'mismatch') return;

        set((s) => ({ extractionStatus: { ...s.extractionStatus, [entity]: 'fetching' } }));

        try {
            const getCancelled = () => get().extractionCancelled;
            let data: SyncPayload['customers'] | SyncPayload['invoices'] | SyncPayload['payments'];

            if (entity === 'customers') {
                data = await fetchAllPages((c) => acombaBridgeService.getCustomers(bridgeUrl, c), getCancelled);
            } else if (entity === 'invoices') {
                // Same source-side start-date bound as startExtraction, so a retried invoice read
                // produces the identical in-window set (not the whole AR history).
                const invoiceSince = await resolveInvoiceExtractionSince();
                data = await fetchAllPages((c) => acombaBridgeService.getInvoiceArs(bridgeUrl, c, 200, invoiceSince), getCancelled);
            } else {
                data = await fetchAllPages((c) => acombaBridgeService.getCustomerPayments(bridgeUrl, c), getCancelled);
            }

            // Re-reading invoices invalidates any links already resolved: they were resolved against the
            // previous invoice set, so keeping them could post a link for an invoice no longer present and
            // miss one for an invoice newly read. Other entities leave the links alone.
            const paymentLinks = entity === 'invoices'
                ? await resolvePaymentLinks(bridgeUrl, data as AcombaInvoiceAr[], getCancelled, get().companyTransphereActive, reportReceiptSubscription(set, get().companyIcIdentifiantPublique))
                : (syncPayload?.paymentLinks ?? null);

            set((s) => ({
                extractionStatus: { ...s.extractionStatus, [entity]: 'done' },
                syncPayload: {
                    // null, not [], when there is no prior payload: nothing has been resolved, and that is
                    // "unknown" rather than "none".
                    ...(syncPayload ?? { customers: [], invoices: [], payments: [], paymentLinks: null }),
                    [entity]: data,
                    paymentLinks,
                },
            }));
        } catch (err) {
            if (err instanceof SessionExpiredError) {
                set({
                    sessionStatus: 'disconnected',
                    sessionErrorMessage: TranslationKey.AcombaBridge.Credentials.SessionExpiredMsg,
                });
                return;
            }
            if (err instanceof InsufficientRightsError) {
                set({
                    sessionStatus: 'disconnected',
                    sessionErrorMessage: TranslationKey.AcombaBridge.Errors.InsufficientRights,
                });
                return;
            }
            set((s) => ({ extractionStatus: { ...s.extractionStatus, [entity]: 'failed' } }));
        }

        const { extractionStatus: updated } = get();
        const isPartial = (['customers', 'invoices', 'payments'] as const).some(
            (e) => updated[e] === 'failed' || updated[e] === 'cancelled',
        );
        set((s) => ({ extractionStatus: { ...s.extractionStatus, isPartial } }));
    },
}));
